export interface DataTableColumnPreferences {
  visibility: Record<string, boolean>;
  order: string[];
  sizing: Record<string, number>;
}

export interface DataTableColumnPreferencesConfig {
  columnOptions: { id: string; label: string }[];
  hiddenByDefault?: string[];
}

const STORAGE_PREFIX = "xgs-data-table";

export function getDataTableColumnPreferencesStorageKey(instanceKey: string): string {
  return `${STORAGE_PREFIX}:${instanceKey}`;
}

export function getDefaultColumnPreferences(
  config: DataTableColumnPreferencesConfig
): DataTableColumnPreferences {
  const hiddenSet = new Set(config.hiddenByDefault ?? []);
  const visibility = config.columnOptions.reduce(
    (acc, { id }) => ({ ...acc, [id]: !hiddenSet.has(id) }),
    {} as Record<string, boolean>
  );
  const order = config.columnOptions.map(({ id }) => id);
  return { visibility, order, sizing: {} };
}

export function mergeColumnPreferences(
  saved: Partial<DataTableColumnPreferences> | null | undefined,
  defaults: DataTableColumnPreferences
): DataTableColumnPreferences {
  const validIds = new Set(defaults.order);

  const order: string[] = [];
  const savedOrder = Array.isArray(saved?.order) ? saved.order : [];
  savedOrder.forEach((id) => {
    if (typeof id === "string" && validIds.has(id) && !order.includes(id)) {
      order.push(id);
    }
  });

  // Insert missing columns at their default relative positions (not always at the end),
  // so newly added columns (e.g. Appt after Pieces) land where product expects.
  defaults.order.forEach((id, defaultIndex) => {
    if (order.includes(id)) return;

    let insertAfter = -1;
    for (let i = defaultIndex - 1; i >= 0; i--) {
      const prevIdx = order.indexOf(defaults.order[i]);
      if (prevIdx !== -1) {
        insertAfter = prevIdx;
        break;
      }
    }

    if (insertAfter !== -1) {
      order.splice(insertAfter + 1, 0, id);
      return;
    }

    let insertBefore = -1;
    for (let i = defaultIndex + 1; i < defaults.order.length; i++) {
      const nextIdx = order.indexOf(defaults.order[i]);
      if (nextIdx !== -1) {
        insertBefore = nextIdx;
        break;
      }
    }

    if (insertBefore !== -1) {
      order.splice(insertBefore, 0, id);
    } else {
      order.push(id);
    }
  });

  const visibility: Record<string, boolean> = { ...defaults.visibility };
  if (saved?.visibility && typeof saved.visibility === "object") {
    defaults.order.forEach((id) => {
      if (typeof saved.visibility![id] === "boolean") {
        visibility[id] = saved.visibility![id];
      }
    });
  }

  const sizing: Record<string, number> = {};
  if (saved?.sizing && typeof saved.sizing === "object") {
    defaults.order.forEach((id) => {
      const width = saved.sizing![id];
      if (typeof width === "number" && Number.isFinite(width) && width > 0) {
        sizing[id] = width;
      }
    });
  }

  return { visibility, order, sizing };
}

function parseSavedPreferences(raw: string): Partial<DataTableColumnPreferences> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const record = parsed as Partial<DataTableColumnPreferences>;
    return {
      visibility:
        record.visibility && typeof record.visibility === "object"
          ? record.visibility
          : undefined,
      order: Array.isArray(record.order) ? record.order : undefined,
      sizing:
        record.sizing && typeof record.sizing === "object" ? record.sizing : undefined,
    };
  } catch {
    return null;
  }
}

export function loadDataTableColumnPreferences(
  instanceKey: string,
  config: DataTableColumnPreferencesConfig
): DataTableColumnPreferences {
  const defaults = getDefaultColumnPreferences(config);
  if (typeof window === "undefined") {
    return defaults;
  }
  try {
    const raw = localStorage.getItem(getDataTableColumnPreferencesStorageKey(instanceKey));
    if (!raw) {
      return defaults;
    }
    const saved = parseSavedPreferences(raw);
    return mergeColumnPreferences(saved, defaults);
  } catch {
    return defaults;
  }
}

export function saveDataTableColumnPreferences(
  instanceKey: string,
  prefs: DataTableColumnPreferences
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(
      getDataTableColumnPreferencesStorageKey(instanceKey),
      JSON.stringify(prefs)
    );
  } catch {
    // ignore quota / private mode
  }
}
